import React, { useEffect, useState } from 'react';
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  Button,
  CheckIcon,
  CloseIcon,
  Text,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useSiteImportUIStore } from '../../stores/siteImportUIStore';
import { useShellStore } from '../../../../shared/stores/shellStore';
import { SIDEBAR_WIDTH } from '../../../../shared/components/layout/sidebar/constants';
import { SiteImportFormData } from '../../schemas/siteImportSchema';
import { SiteImportPreviewResponse } from '../../api/sitesApi';
import { Routes } from '../../../../routes';

/** Minimal matchMedia hook replacing MUI useMediaQuery for layout flags. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [query]);

  return matches;
}

const stepLabels = [
  'Site Details',
  'Choose Source',
  'Choose Template',
  'Review & Confirm',
];

interface SiteImportBottomToolbarProps {
  previewData: SiteImportPreviewResponse | null;
  selectedTemplateId: string | null;
  allLabourItemsResolved: boolean;
  uploading: boolean;
  importing: boolean;
  onImport: () => void;
  onCancel: () => void;
  onTemplateConfirm: () => Promise<void>;
}

const SiteImportBottomToolbar: React.FC<SiteImportBottomToolbarProps> = ({
  previewData,
  selectedTemplateId,
  allLabourItemsResolved,
  uploading,
  importing,
  onImport,
  onCancel,
  onTemplateConfirm,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const navigate = useNavigate();
  const form = useFormContext<SiteImportFormData>();
  const sidebarCollapsed = useShellStore(state => state.sidebarCollapsed);

  const {
    activeStep,
    previousStep,
    nextStep,
    importPath,
    setShowCancelConfirm,
  } = useSiteImportUIStore();

  const currentStepLabels = [...stepLabels];
  if (importPath === 'csv') {
    currentStepLabels[2] = 'Upload CSV';
  }

  const canGoNext = () => {
    switch (activeStep) {
      case 0: {
        const { errors } = form.formState;
        const values = form.getValues();
        return (
          !errors.name &&
          !errors.slug &&
          !errors.what3words &&
          (values.name?.length ?? 0) >= 3 &&
          (values.slug?.length ?? 0) === 4
        );
      }
      case 1:
        return importPath !== null;
      case 2:
        if (importPath === 'csv') {
          return Boolean(previewData);
        }
        return selectedTemplateId !== null;
      case 3: {
        const hasMissing =
          (previewData?.missingLabourDefinitions?.length ?? 0) > 0;
        if (hasMissing && !allLabourItemsResolved) return false;
        const hasBlockingErrors =
          previewData?.hasErrors && !allLabourItemsResolved;
        return Boolean(previewData && !hasBlockingErrors && !importing);
      }
      default:
        return false;
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      const { isDirty } = form.formState;
      if (isDirty || importPath !== null) {
        setShowCancelConfirm(true);
      } else {
        navigate(Routes.sites.manage);
      }
    } else {
      previousStep();
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const valid = await form.trigger(['name', 'slug', 'what3words', 'notes']);
      if (!valid) return;
      nextStep();
      return;
    }
    if (activeStep === 2 && importPath === 'template') {
      await onTemplateConfirm();
      return;
    }
    nextStep();
  };

  const isLastStep = activeStep === 3;
  const canAdvance = canGoNext();
  const loading = uploading || importing;
  const sidebarOffset = isDesktop && !sidebarCollapsed ? SIDEBAR_WIDTH : 0;

  return (
    <div
      className="fixed bottom-0 z-40 border-t border-border-default bg-surface-default shadow-lg transition-[width,left] duration-300"
      style={{ left: sidebarOffset, width: `calc(100% - ${sidebarOffset}px)` }}
      data-testid="site-import-bottom-toolbar"
    >
      <div className="flex min-h-12 items-center justify-between px-4">
        {/* Left: Back + step indicator */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={loading}
            className={isMobile ? 'min-w-10 px-0' : undefined}
            data-testid="stepper-back-button"
          >
            <ArrowBackIcon className="size-4" aria-hidden="true" />
            {isMobile ? '' : 'Back'}
          </Button>

          <Text
            variant="body2"
            color="secondary"
            data-testid="stepper-step-indicator"
          >
            {activeStep + 1}/4 {currentStepLabels[activeStep]}
          </Text>
        </div>

        {/* Right: Cancel + Primary */}
        <div className="flex items-center gap-4">
          <Button
            variant="outlineDanger"
            onClick={onCancel}
            className={isMobile ? 'min-w-10 px-0' : undefined}
            data-testid="stepper-cancel-button"
          >
            <CloseIcon className="size-4" aria-hidden="true" />
            {isMobile ? '' : 'Cancel'}
          </Button>

          {isLastStep ? (
            <Button
              variant="success"
              onClick={onImport}
              disabled={!canAdvance || loading}
              className={`font-bold ${isMobile ? 'min-w-10 px-0' : 'px-6'}`}
              data-testid="stepper-confirm-import-button"
            >
              <CheckIcon className="size-4" aria-hidden="true" />
              {isMobile ? '' : importing ? 'Creating...' : 'Create site'}
            </Button>
          ) : (
            <Button
              variant="neutral"
              onClick={handleNext}
              disabled={!canAdvance || loading}
              className={isMobile ? 'min-w-10 px-0' : undefined}
              data-testid="stepper-next-button"
            >
              <ArrowForwardIcon className="size-4" aria-hidden="true" />
              {isMobile ? '' : 'Continue'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteImportBottomToolbar;
